import { type FormEvent, useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import useAuth from "../../services/useAuth";
import TokenManager from "../../commons/TokenManager";
import {
    getAuthenticatedRedirectUrl,
    getReturnToPath,
    removeSearchParamFromCurrentUrl,
} from "../../commons/helpers";
import { useAuthContext } from "../../contexts/Auth";
import { useGoogleOauth } from "../../contexts/GoogleOauth";

const theme = createTheme();

export default function SignIn() {
    const [emailOrUsername, setEmailOrUsername] = useState("");
    const [password, setPassword] = useState<string>("");
    const [showPassword, setShowPassword] = useState(false);
    const [stayConnect, setStayConnect] = useState(true);
    const [returnToPath] = useState<string | undefined>(() => {
        const searchParams = new URLSearchParams(window.location.search);
        return getReturnToPath(searchParams.get("returnTo"));
    });

    const { getAccessToken, getTokenWithGoogle } = useAuth();
    const { isAuthenticated, setIsAuthenticated } = useAuthContext();
    const { googleCredentials } = useGoogleOauth();

    const send = async () => {
        const response = await getAccessToken({
            emailOrUsername: emailOrUsername.trim(),
            password: password.trim(),
        });

        if (response) {
            TokenManager.setAccessToken(response.accessToken, response.refreshToken);
            setIsAuthenticated(true);
        }
    };

    useEffect(() => {
        const authenticate = async () => {
            if (!googleCredentials) {
                return;
            }

            const response = await getTokenWithGoogle({
                token: googleCredentials,
            });

            if (response) {
                TokenManager.setAccessToken(response.accessToken, response.refreshToken);
                setIsAuthenticated(true);
            }
        };

        authenticate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleCredentials]);

    useEffect(() => {
        TokenManager.setStayConnect(stayConnect);
    }, [stayConnect]);

    useEffect(() => {
        removeSearchParamFromCurrentUrl("returnTo");
    }, []);

    useEffect(() => {
        const container = document.getElementById("googleButton");

        window.google.accounts.id.renderButton(
            container,
            { theme: "outline", size: "large", width: container?.offsetWidth } // Customização básica
        );
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            const tokenData = TokenManager.getTokenData();
            window.location.replace(
                getAuthenticatedRedirectUrl(Boolean(tokenData.validated), returnToPath)
            );
        }
    }, [isAuthenticated, returnToPath]);

    return (
        <ThemeProvider theme={theme}>
            <Container
                component="main"
                maxWidth="xs"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    height: "100vh",
                }}
            >
                <CssBaseline />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: "blue" }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        Login
                    </Typography>

                    <Box
                        component="form"
                        onSubmit={(e: FormEvent<HTMLFormElement>) => {
                            e.preventDefault();
                            send();
                        }}
                        sx={{ mt: 1 }}
                    >
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="emailOrUsername"
                            label="email ou username"
                            name="emailOrUsername"
                            autoComplete="emailOrUsername"
                            autoFocus
                            value={emailOrUsername}
                            onChange={(event) => {
                                setEmailOrUsername(event.target.value);
                            }}
                            inputProps={{
                                "data-test-id": "input-email",
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="senha"
                            type={showPassword ? "text" : "password"}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(event) => {
                                setPassword(event.target.value);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={() => {
                                            setShowPassword(!showPassword);
                                        }}
                                    >
                                        {showPassword ? <VisibilityIcon /> : <VisibilityOff />}
                                    </IconButton>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    value="Permanecer conectado"
                                    checked={stayConnect}
                                    onChange={() => {
                                        setStayConnect(!stayConnect);
                                    }}
                                    color="primary"
                                />
                            }
                            label="Permanecer conectado"
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            type="submit"
                            data-testid="button-login"
                        >
                            Login
                        </Button>
                        <Box sx={{ mt: 1, mb: 1, width: "100%" }}>
                            <div id="googleButton"></div>
                        </Box>

                        <Stack spacing={1} sx={{ alignItems: "center" }}>
                            <Link component={RouterLink} to="/register" underline="hover">
                                Criar conta
                            </Link>
                            <Link component={RouterLink} to="/forgot-password" underline="hover">
                                Recuperar senha
                            </Link>
                        </Stack>
                    </Box>
                </Box>
            </Container>
        </ThemeProvider>
    );
}
